import React, { useEffect, useRef } from 'react';

/**
 * The dropdown shell the Month Planner's Course and Chapter pickers share.
 *
 * Both are buttons rather than `<select>`s, so the whole field opens the list —
 * a native select only reacted to its arrow region here, and dragging on its
 * face selected the paragraph behind it instead of opening anything.
 *
 * Renders the trigger and, while open, a panel below it. Clicking outside or
 * pressing Escape closes, the way the select it replaced did.
 */
const PickerPopover = ({
  label,
  value,
  placeholder,
  open,
  onOpenChange,
  disabled = false,
  children,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDocumentClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onOpenChange(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onOpenChange(false);
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div className="sb-field sb-picker" ref={containerRef}>
      <span className="sb-fieldlabel">{label}</span>
      <button
        type="button"
        className="sb-pickertrigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${label}: ${value || placeholder}`}
        disabled={disabled}
        onClick={() => onOpenChange(!open)}
      >
        <span className={`sb-pickervalue${value ? '' : ' placeholder'}`}>
          {value || placeholder}
        </span>
      </button>

      {open && !disabled && <div className="sb-pickerpanel">{children}</div>}
    </div>
  );
};

export default PickerPopover;
